/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {BulkActionEntity} from '@ion/core/src/types/entities';
import FormCheckBoxWithIndicator from '../../components/forms/FormCheckBoxWithIndicator';
import {FormItem} from '../../components/forms/FormItem';
import {FormState} from '../../hooks/useFormState';

export enum MoveDetailFields {
  TARGET_ACCOUNT = 'account',
  ENABLE_FORCE_MOVE = 'force'
};

export interface ForceMoveEnablementProps {
  formState: FormState;
  onChange: (newState: FormState) => void;
  entity?: BulkActionEntity;
};

export const isValidMoveEntity = (entity: string): boolean => (
  isDevice(entity)
);

export const isDevice = (entity: string): boolean => (
  entity === BulkActionEntity.DEVICES
);

export const isAsset = (entity: string): boolean => (
  entity === BulkActionEntity.ASSETS
);

const useStyles = makeStyles(() => ({
  checkBoxWrapper : {
    padding : '20px 10px 0 5px'
  }
}));

/**
 * Re-usable checkbox component to enable/disable Force moving an entity.
 */
const ForceMoveEnablement = ({entity, formState, onChange}: ForceMoveEnablementProps) => {
  const classes = useStyles();
  const {label, tooltip} = translate('common:editors.bulkActions.forceMove', {
    associatedEntity : isDevice(entity) ? BulkActionEntity.ASSETS: BulkActionEntity.DEVICES,
    returnObjects    : true,
    entity
  });

  const onCheck = useCallback(({target: {checked: value}}: ChangeEvent<HTMLInputElement>) => {
    const name = MoveDetailFields.ENABLE_FORCE_MOVE;
    onChange({
      [name] : {
        required : false,
        value,
        name
      }
    });
  }, [entity, formState, onChange]);

  if (isValidMoveEntity(entity)) {
    const checked = formState[MoveDetailFields.ENABLE_FORCE_MOVE]?.value || false;
    return (
      <FormItem className={classes.checkBoxWrapper}>
        <FormCheckBoxWithIndicator checked={checked}
          onChange={onCheck}
          information={tooltip}
          label={label} />
      </FormItem>
    );
  }
};

export default ForceMoveEnablement;
